'use client';
import AppIcon from '../../App/AppIcon';
import { useState, useEffect, useRef } from 'react';
import "./style.css";

type Props = {
  label?: string;
  name?: string;
  value?: File | File[] | null;
  error?: string;
  className?: string;
  containerClassName?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  multiple?: boolean;
  inline?: boolean; // Makes the input display clickable, hiding the separate button
  onChange?: (file: File | File[] | null) => void;
  chooseLabel?: string;
  removeLabel?: string;
  changeLabel?: string;
};

const InputFile = ({
  label,
  name,
  value = null,
  error,
  className,
  containerClassName,
  required,
  placeholder = 'Nenhum arquivo selecionado',
  disabled,
  accept,
  maxSize,
  multiple = false,
  inline = false,
  onChange,
  chooseLabel = 'Escolher Arquivo',
  removeLabel = 'Remover',
  changeLabel = 'Trocar Arquivo',
}: Props) => {
  const [selectedFiles, setSelectedFiles] = useState<File | File[] | null>(value);
  const [internalError, setInternalError] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    
    if (files && files.length > 0) {
      if (multiple) {
        const fileArray = Array.from(files);
        
        // Check if any file exceeds max size
        if (maxSize) {
          const oversizedFile = fileArray.find((file: File) => file.size > maxSize * 1024 * 1024);
          if (oversizedFile) {
            setInternalError(`O arquivo "${oversizedFile.name}" não pode exceder ${maxSize}MB.`);
            setSelectedFiles(null);
            onChange && onChange(null);
            return;
          }
        }
        
        setInternalError(undefined);
        setSelectedFiles(fileArray);
        onChange && onChange(fileArray);
      } else {
        const file = files[0];
        
        if (maxSize && file.size > maxSize * 1024 * 1024) {
          setInternalError(`O arquivo não pode exceder ${maxSize}MB.`);
          setSelectedFiles(null);
          onChange && onChange(null);
          return;
        }
        
        setInternalError(undefined);
        setSelectedFiles(file);
        onChange && onChange(file);
      }
    }
  };

  const handleRemove = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedFiles(null);
    setInternalError(undefined);
    onChange && onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (Array.isArray(selectedFiles)) {
      const newFiles = selectedFiles.filter((_, i) => i !== index);
      const updatedFiles = newFiles.length > 0 ? newFiles : null;
      setSelectedFiles(updatedFiles);
      onChange && onChange(updatedFiles);
      
      if (inputRef.current && newFiles.length === 0) {
        inputRef.current.value = '';
      }
    }
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  // Update internal state when value prop changes
  useEffect(() => {
    setSelectedFiles(value);
  }, [value]);

  const displayError = error || internalError;
  const hasFiles = multiple 
    ? Array.isArray(selectedFiles) && selectedFiles.length > 0 
    : selectedFiles !== null;

  return (
    <div className={`input-file-container ${containerClassName || ''}`}>
      {label && (
        <label htmlFor={name} className={`input-file-label ${displayError ? 'error' : ''}`}>
          {label}
          {required && <span className="input-file-required"> *</span>}
        </label>
      )}
      
      <div className="input-file-content">
        <input
          type="file"
          ref={inputRef}
          id={name}
          name={name}
          accept={accept}
          disabled={disabled}
          multiple={multiple}
          onChange={handleFileChange}
          className="input-file-input"
        />
        
        {multiple && Array.isArray(selectedFiles) && selectedFiles.length > 0 ? (
          <div className="input-file-list">
            {selectedFiles.map((file, index) => (
              <div key={index} className={`input-file-item ${displayError ? 'error' : ''} ${disabled ? 'disabled' : ''} ${className || ''}`}>
                <span className="input-file-name">{file.name}</span>
                <button
                  type="button"
                  onClick={(e) => handleRemoveFile(index, e)}
                  disabled={disabled}
                  className={`input-file-item-remove ${disabled ? 'disabled' : ''}`}
                  title={removeLabel}
                >
                  X
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div 
            className={`input-file-display ${displayError ? 'error' : ''} ${disabled ? 'disabled' : ''} ${inline && !disabled ? 'clickable' : ''} ${className || ''}`}
            onClick={inline && !disabled ? handleButtonClick : undefined}
          >
            <span className="input-file-name">
              {!multiple && selectedFiles instanceof File ? selectedFiles.name : placeholder}
            </span>
            {inline && !multiple && selectedFiles && (
              <button
                type="button"
                onClick={(e) => handleRemove(e)}
                disabled={disabled}
                className={`input-file-inline-remove ${disabled ? 'disabled' : ''}`}
                title={removeLabel}
              >
                X
              </button>
            )}
          </div>
        )}

        {!inline && (
          <div className="input-file-buttons">
            <button
              type="button"
              onClick={handleButtonClick}
              disabled={disabled}
              className={`input-file-button input-file-button-choose ${disabled ? 'disabled' : ''}`}
            >
              {hasFiles ? changeLabel : chooseLabel}
            </button>
            
            {!multiple && selectedFiles && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={disabled}
                className={`input-file-button input-file-button-remove ${disabled ? 'disabled' : ''}`}
              >
                <AppIcon icon="pi-trash" />
              </button>
            )}
            
            {multiple && Array.isArray(selectedFiles) && selectedFiles.length > 0 && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={disabled}
                className={`input-file-button input-file-button-remove ${disabled ? 'disabled' : ''}`}
              >
                <AppIcon icon="pi-trash" />
              </button>
            )}
          </div>
        )}
      </div>

      {displayError && <span className="input-file-error">{displayError}</span>}
    </div>
  );
};

export default InputFile;

